import { useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { F0Alert, F0Box } from '@factorialco/f0-react'
import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { DiscardOutdatedTaskDocument } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import {
  TaskActionsHook,
  TaskCustomComponent,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { trackDiscardInboxTask } from 'modules/inbox_tasks/lib/events'
import { DiscardWarningAlert } from 'modules/inbox_tasks/tasks/discard/DiscardWarningAlert'

import { useEmployeeTableViewRow } from 'modules/inbox_tasks/activities/Task/utils/content'

const DAYS_IN_WEEK = 7

const TerminateEmployeeContent: TaskCustomComponent<'EmployeesEmployeeName'> = ({
  affectee,
  assigneesTotalCount,
}) => {
  const employeeRow = useEmployeeTableViewRow({
    title: i18n.t('tasks.labels.employee'),
    employee: affectee,
  })

  const details = [employeeRow].filter(Boolean)

  return (
    <F0Box display="flex" flexDirection="column" gap="lg">
      {details.length > 0 && <DetailsItemsList details={details} tableView />}
      <F0Alert
        variant="info"
        title={i18n.t('tasks.terminate_employee.alert_title')}
        description={i18n.t('tasks.terminate_employee.alert_description')}
      />
      <DiscardWarningAlert assigneesTotalCount={assigneesTotalCount} />
    </F0Box>
  )
}

const useTerminateEmployeeActions: TaskActionsHook<'EmployeesEmployeeName'> = ({
  taskId,
  taskable,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [discardTask] = useMutation(DiscardOutdatedTaskDocument, {
    extractorFn: (data) => data.tasksMutations.discardOutdatedTasksInboxTask,
  })

  const onClickPrimaryAction = useCallback(async () => {
    const employeeId = taskable?.id

    if (!employeeId) return false

    const url = navigation.employees.id.terminate.fullPath({
      id: String(employeeId),
    })

    navigate(url)
    return true
  }, [taskable, navigation, navigate])

  const onClickDropdownAction = useCallback(
    async ({ actionId }: { taskId: string; actionId: string }) => {
      if (actionId === 'discard') {
        trackDiscardInboxTask({ category: undefined })
        await discardTask({ variables: { taskId } })
        return true
      }
      return false
    },
    [discardTask, taskId]
  )

  return {
    onClickPrimaryAction,
    onClickDropdownAction,
    dropdownActions: [
      {
        value: 'discard',
        label: i18n.t('tasks.mark.as', {
          status: i18n.t('tasks.status.discarded').toLowerCase(),
        }),
      },
    ],
  }
}

export const TerminateEmployeeTaskDefinition: TaskDefinition<'EmployeesEmployeeName'> = {
  taskableTypename: 'EmployeesEmployeeName',

  component: TerminateEmployeeContent,
  useActions: useTerminateEmployeeActions,

  getTitle: ({ info, dueOn }) => {
    const employeeName = info.fullName ?? null

    if (dueOn) {
      const dueDate = new Date(dueOn)
      const today = new Date()
      const diffInDays = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))

      if (diffInDays > DAYS_IN_WEEK) {
        return i18n.t('employees.tasks.terminate.title_before_soon', {
          name: employeeName,
        })
      }
    }

    return i18n.t('employees.tasks.terminate.title_before', {
      name: employeeName,
    })
  },

  getMetadata: () => {
    return []
  },
}
